/**
 * PricingForm — simplified pricing UX shared by the publish wizard
 * and the edit page.
 *
 * Flow:
 *   1. Free or Paid?           (big toggle / tile pair)
 *   2. If Paid: which model?   (One-time · Subscription · Both)
 *   3. Show only the price field(s) the chosen model needs.
 *
 * Maps onto the wire-format fields the BE accepts:
 *   * ``price_monthly_credits`` (>= 0)
 *   * ``price_onetime_credits`` (>= 0)
 *
 * Earnings accrue to the seller's earnings ledger and are paid out
 * by the platform team — there is no payout-method choice.
 */

import React from 'react';
import {
  Box, InputAdornment, Paper, Stack, TextField, Typography,
  alpha, useTheme,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PaidIcon from '@mui/icons-material/Paid';

import {
  dailyFromMonthly,
  usdFromCredits, creditsFromUsd, formatUSD, useUsdRate,
} from './marketplaceUtils';


// Compact tile - title + tiny sub. Active tile gets a primary
// border, no oversized icon block to keep the row low-profile.
const ChoiceTile = ({ icon, title, sub, active, onClick, accent }) => {
  const theme = useTheme();
  const color = accent || theme.palette.primary.main;
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        flex: 1,
        p: 1.25,
        cursor: 'pointer',
        borderRadius: 1.5,
        borderWidth: active ? 2 : 1,
        borderColor: active ? color : 'divider',
        bgcolor: active ? alpha(color, 0.04) : 'background.paper',
        transition: 'border-color 120ms ease, background-color 120ms ease',
        '&:hover': { borderColor: color },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        {icon && (
          <Box sx={{
            width: 22, height: 22, borderRadius: 0.75,
            bgcolor: active
              ? alpha(color, 0.14)
              : alpha(theme.palette.text.primary, 0.04),
            color: active ? color : 'text.secondary',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography fontWeight={700} sx={{ fontSize: 13, lineHeight: 1.2 }}>
            {title}
          </Typography>
          {sub && (
            <Typography variant="caption" color="text.secondary"
                        sx={{
                          display: 'block', fontSize: 11,
                          lineHeight: 1.3, mt: 0.15,
                        }}>
              {sub}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};


// ---- Pricing model choices (only relevant when "Paid" is on) ----------

const MODEL_OPTIONS = [
  { value: 'subscription', title: 'Subscription',          sub: 'Recurring monthly fee, charged daily.' },
  { value: 'onetime',      title: 'One-time fee',          sub: 'Pay once, keep access.' },
  { value: 'hybrid',       title: 'One-time + Subscription', sub: 'Upfront fee + recurring.' },
];


const PricingForm = ({ form, set, setMany }) => {
  const theme = useTheme();
  // Conversion rate from BE settings cache.
  const usdRate = useUsdRate();

  // Internal storage stays in CREDITS (the BE field names + wire
  // format). The inputs below are in USD, converted both ways via
  // ``usdRate``.
  const monthly = Number(form.price_monthly_credits) || 0;
  const onetime = Number(form.price_onetime_credits) || 0;
  const isFree = monthly <= 0 && onetime <= 0;

  // Active "model" the user picked (only meaningful when paid).
  const model = form.pricing_model || (
    isFree ? 'free'
      : (onetime > 0 && monthly > 0) ? 'hybrid'
      : (onetime > 0)                ? 'onetime'
      :                                'subscription'
  );

  // Default starter prices when the user toggles into paid (in
  // CREDITS, since that's the wire format). 1000cr ≈ $10 / 5000cr ≈ $50.
  const DEFAULT_MONTHLY_CREDITS = 1000;
  const DEFAULT_ONETIME_CREDITS = 5000;

  const setFree = () => setMany({
    pricing_model:         'free',
    price_monthly_credits: 0,
    price_onetime_credits: 0,
  });

  const setPaid = () => setMany({
    pricing_model:         'subscription',
    price_monthly_credits: monthly > 0 ? monthly : DEFAULT_MONTHLY_CREDITS,
    price_onetime_credits: 0,
  });

  const pickModel = (next) => {
    if (next === 'subscription') {
      setMany({
        pricing_model: 'subscription',
        price_monthly_credits: monthly > 0 ? monthly : DEFAULT_MONTHLY_CREDITS,
        price_onetime_credits: 0,
      });
    } else if (next === 'onetime') {
      setMany({
        pricing_model: 'onetime',
        price_monthly_credits: 0,
        price_onetime_credits: onetime > 0 ? onetime : DEFAULT_ONETIME_CREDITS,
      });
    } else {
      setMany({
        pricing_model: 'hybrid',
        price_monthly_credits: monthly > 0 ? monthly : DEFAULT_MONTHLY_CREDITS,
        price_onetime_credits: onetime > 0 ? onetime : DEFAULT_ONETIME_CREDITS,
      });
    }
  };

  const daily = dailyFromMonthly(monthly);

  // ----- USD-facing input helpers ----------------------------------------
  // The user types dollars; we round-trip to credits behind the
  // scenes. Empty string is preserved while typing so the field
  // doesn't constantly snap to "0".
  const monthlyUsd = monthly > 0 ? usdFromCredits(monthly, usdRate) : '';
  const onetimeUsd = onetime > 0 ? usdFromCredits(onetime, usdRate) : '';

  const handleUsdChange = (key) => (e) => {
    const raw = e.target.value;
    if (raw === '') { set(key, 0); return; }
    const usd = Number(raw);
    if (!Number.isFinite(usd) || usd < 0) return;
    set(key, creditsFromUsd(usd, usdRate));
  };

  return (
    <Stack spacing={1.5}>
      {/* ── Free vs Paid ── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <ChoiceTile
          icon={<LocalOfferIcon sx={{ fontSize: 14 }} />}
          title="Free"
          sub="Anyone can subscribe at no cost."
          active={isFree}
          onClick={setFree}
          accent={theme.palette.success.main}
        />
        <ChoiceTile
          icon={<PaidIcon sx={{ fontSize: 14 }} />}
          title="Paid"
          sub="Charge buyers for access to this signal."
          active={!isFree}
          onClick={setPaid}
        />
      </Stack>

      {/* ── Paid: model picker + price fields ── */}
      {!isFree && (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            {MODEL_OPTIONS.map((m) => (
              <ChoiceTile
                key={m.value}
                title={m.title}
                sub={m.sub}
                active={model === m.value}
                onClick={() => pickModel(m.value)}
              />
            ))}
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            {(model === 'onetime' || model === 'hybrid') && (
              <TextField
                fullWidth size="small"
                label="One-time fee (USD)"
                type="number"
                inputProps={{ step: '0.01', min: 0 }}
                value={onetimeUsd}
                onChange={handleUsdChange('price_onetime_credits')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            )}
            {(model === 'subscription' || model === 'hybrid') && (
              <TextField
                fullWidth size="small"
                label="Monthly price (USD)"
                type="number"
                inputProps={{ step: '0.01', min: 0 }}
                value={monthlyUsd}
                onChange={handleUsdChange('price_monthly_credits')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  endAdornment:   <InputAdornment position="end">/ mo</InputAdornment>,
                }}
                helperText={monthly > 0
                  ? `≈ ${formatUSD(usdFromCredits(daily, usdRate))} / day`
                  : null}
              />
            )}
          </Stack>

          <Typography variant="caption" color="text.disabled"
                      sx={{ fontSize: 11 }}>
            Earnings accrue to your balance per your organization's
            revenue-share agreement.
          </Typography>
        </>
      )}
    </Stack>
  );
};

export default PricingForm;
